import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { isFilterSet, getFilterFromQS } from 'redux/modules/browse';
import { load as loadAllRoles } from 'redux/modules/suggestedRoles';
import { load as loadAllSkills } from 'redux/modules/suggestedSkills';

import Dropdown from 'components/Dropdown/Dropdown';
import ResetFilter from 'components/Browse/Filter/Reset/ResetFilter';
import DayRateFilter from 'components/Browse/Filter/DayRate/DayRateFilter';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import Panel from 'components/Panel/Panel';
import FilterHeader from './FilterHeader/FilterHeader';

import styles from './BrowseFilter.module.scss';
import { withRouter } from 'react-router-dom';

const DEBOUNCE_TIMER = 100; // 0.1s
const ROLE_TYPE = 'role';
const SKILL_TYPE = 'skill';

@connect(
  (state) => ({
    // Roles
    allRoles: state.suggestedRoles.suggestedRoles,
    loadedAllRoles: state.suggestedRoles.loaded,
    loadingAllRoles: state.suggestedRoles.loading,

    searchRoleEntities: state.entities.searchRoles,
    suggestedRoles: state.suggestedRoles2.suggestedRoles,

    // Skills
    allSkills: state.suggestedSkills.suggestedSkills,
    loadedAllSkills: state.suggestedSkills.loaded,
    loadingAllSkills: state.suggestedSkills.loading,

    searchSkillEntities: state.entities.searchSkills,
    suggestedSkills: state.suggestedSkills2.suggestedSkills,
  }),
  {
    loadAllRoles,
    loadAllSkills,
  }
)
@withRouter
export default class BrowseFilter extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Query string
      roleTerm: props.location.query.role || '',
      skillTerm: props.location.query.skill || '',
      locationTerm: props.location.query.location || '',
      minDayRate: props.location.query.minDayRate || '',
      maxDayRate: props.location.query.maxDayRate || '',
      pageTerm: props.location.query.page || 1,

      // Intermediary state
      currentMinRate: Number(props.location.query.minDayRate),
      currentMaxRate: Number(props.location.query.maxDayRate),
      suggestedRoles: [],
      suggestedSkills: [],

      // Dropdowns
      roleFocus: false,
      roleSelected: !!props.location.query.role,
      skillFocus: false,
      skillSelected: !!props.location.query.skill,
      locationSelected: !!props.location.query.location,
      roleHighlightIndex: 0,
      skillHighlightIndex: 0,

      // Expand on page load if there are no filters
      // and hence there is no search string in the url
      expanded: !props.location.search,
    };
  }

  componentDidMount() {
    // Load Roles
    const { loadAllRoles, loadedAllRoles, loadingAllRoles } = this.props;
    if (!loadedAllRoles && !loadingAllRoles) {
      loadAllRoles();
    }

    // Load Skills
    const { loadAllSkills, loadedAllSkills, loadingAllSkills } = this.props;
    if (!loadedAllSkills && !loadingAllSkills) {
      loadAllSkills();
    }
  }

  componentDidUpdate(_, prevState) {
    const { roleTerm, skillTerm } = this.state;
    const { allRoles, allSkills } = this.props;

    if (prevState.roleTerm !== roleTerm) {
      this.setSuggestedResults(roleTerm, allRoles, ROLE_TYPE);
    }

    if (prevState.skillTerm !== skillTerm) {
      this.setSuggestedResults(skillTerm, allSkills, SKILL_TYPE);
    }
  }

  setSuggestedResults = (term = '', allItems = [], type = ROLE_TYPE) => {
    const capitalisedType = type[0].toUpperCase() + type.substr(1);

    const idxKey = `${type}HighlightIndex`;
    const updateKey = `suggested${capitalisedType}s`;
    const timerKey = `_suggested${capitalisedType}sTimeout`;

    // Clear debouncer (since input has changed in the meantime)
    if (this[timerKey]) {
      clearTimeout(this[timerKey]);
    }

    if (term.length > 0) {
      this._suggestedRolesTimeout = setTimeout(() => {
        const results = this.filterDropdownItems(term, allItems);

        this.setState({
          [idxKey]: 0,
          [updateKey]: results,
        });
      }, DEBOUNCE_TIMER);
    } else {
      this.setState({
        [idxKey]: 0,
        [updateKey]: [],
      });
    }
  };

  filterDropdownItems = (term = '', allItems = [], limit = null) => {
    const resultLimit = limit || allItems.length;
    return allItems.filter(
      function (item) {
        const filterCondition = () =>
          item.name.toLowerCase().includes(term.toLowerCase()) > 0;

        if (this.count < resultLimit && filterCondition()) {
          this.count++;
          return true;
        }
        return false;
      },
      { count: 0 }
    );
  };

  // Timeouts
  static _suggestedRolesTimeout = null;
  static _suggestedSkillsTimeout = null;

  // Event handlers
  handleDropdownKeyDown = (type = ROLE_TYPE, event) => {
    if (type !== ROLE_TYPE && type !== SKILL_TYPE) {
      return false;
    }

    const updateKey = `${type}HighlightIndex`;
    const {
      [`${type}HighlightIndex`]: highlightIdx,
      [`suggested${type[0].toUpperCase() + type.substr(1)}s`]: suggestedItems,
    } = this.state;

    // Enter Key
    if (event.key === 'Enter' && suggestedItems.length > 0) {
      event.preventDefault();
      event.target.blur();

      this.handleRoleSkill(type, suggestedItems[highlightIdx].name);
      return false;
    }

    // Arrow Down
    if (event.key === 'ArrowDown' && highlightIdx < suggestedItems.length - 1) {
      event.preventDefault();

      this.setState({ [updateKey]: highlightIdx + 1 });
      return false;
    }

    // Arrow Up
    if (event.key === 'ArrowUp' && highlightIdx > 0) {
      event.preventDefault();

      this.setState({ [updateKey]: highlightIdx - 1 });
      return false;
    }
  };

  handleRoleSkill = (type = ROLE_TYPE, value = '') => {
    this.setState(
      {
        [`${type}Selected`]: !(value === ''),
        [`${type}Term`]: value,
        expanded: false,
      },
      () =>
        this.handleSearch({
          filterType: type,
          filterValue: value,
        })
    );
  };

  handleSelectLocation = (location) => {
    const { city, country, text } = location;
    const locationTerm = text || (city ? `${city}, ${country}` : country);

    this.setState(
      { locationSelected: true, locationTerm, expanded: false },
      () => {
        this.handleSearch({
          filterType: 'location',
          filterValue: locationTerm,
        });
      }
    );
  };

  handleSelectDayRate = (minDayRate, maxDayRate) => {
    this.setState(
      {
        minDayRate,
        maxDayRate,
        currentMinRate: Number(minDayRate) || null,
        currentMaxRate: Number(maxDayRate) || null,
        expanded: false,
      },
      () =>
        this.handleSearch({
          filterType: 'dayRate',
          filterValue: {
            min: minDayRate,
            max: maxDayRate,
          },
        })
    );
  };

  handleSelectCustomRate = () => {
    const { currentMinRate, currentMaxRate } = this.state;

    if (currentMinRate && currentMaxRate) {
      if (Number(currentMinRate) > Number(currentMaxRate)) {
        // Invert values if user mistakes min for max
        this.setState(
          { minDayRate: currentMaxRate, maxDayRate: currentMinRate },
          () => this.handleSelectDayRate(currentMaxRate, currentMinRate)
        );
      }
    }

    this.handleSelectDayRate(currentMinRate, currentMaxRate);
  };

  handleCustomRateChange = (type, value) => {
    this.setState({ [type]: Number(value) });
  };

  handleSearch = ({ filterType, filterValue }) => {
    // Get current filter based on query string
    const { history, location } = this.props;
    const currentQuery = queryString.parse(location.search);

    const newQuery = {
      ...currentQuery,
      page: 1, // Reset pagination
    };

    if (filterType === 'dayRate') {
      newQuery.minDayRate = filterValue.min;
      newQuery.maxDayRate = filterValue.max;
    } else {
      newQuery[filterType] = filterValue;
    }

    Object.keys(newQuery).forEach(
      (key) => !newQuery[key] && delete newQuery[key]
    );

    history.push(
      queryString.stringifyUrl({
        url: '/browse',
        query: isFilterSet(newQuery) ? newQuery : {},
      })
    );
  };

  handleLocationFilterReset = () => {
    const { history, location } = this.props;

    const query = queryString.parse(location.search);
    delete query.location;

    this.setState(
      {
        locationSelected: false,
        locationTerm: '',
        expanded: false,
      },
      () => {
        history.push(
          queryString.stringifyUrl({
            url: '/browse',
            query,
          })
        );
      }
    );
  };

  handleFilterReset = () => {
    const { history } = this.props;

    this.setState(
      {
        roleTerm: '',
        skillTerm: '',
        locationTerm: '',
        minDayRate: '',
        maxDayRate: '',
        pageTerm: 1,
        currentMinRate: '',
        currentMaxRate: '',
        roleFocus: false,
        roleSelected: false,
        skillFocus: false,
        skillSelected: false,
        locationSelected: false,
        roleHighlightIndex: 0,
        skillHighlightIndex: 0,
        expanded: false,
      },
      () => {
        history.push('/browse');
      }
    );
  };

  // Render methods
  renderDropdownItem = (item) => (
    <div className={styles.dropdownItem}>{item.name}</div>
  );

  render() {
    // Role Filter
    const {
      locationSelected,
      roleHighlightIndex,
      roleFocus,
      roleSelected,
      roleTerm,
      suggestedRoles: roleSuggestions,
    } = this.state;

    // Skill Filter
    const {
      skillHighlightIndex,
      skillFocus,
      skillSelected,
      skillTerm,
      suggestedSkills: skillSuggestions,
    } = this.state;

    // Location Filter
    const { locationTerm } = this.state;

    // Day Rate Filter
    const { minDayRate, maxDayRate, currentMinRate, currentMaxRate, expanded } =
      this.state;

    let numberOfFilters = 0;
    if (skillSelected) numberOfFilters++;
    if (roleSelected) numberOfFilters++;
    if (locationSelected) numberOfFilters++;
    if (minDayRate) numberOfFilters++;
    if (maxDayRate) numberOfFilters++;

    return (
      <Panel
        className={`${styles.container} ${expanded ? styles.expanded : ''}`}
      >
        {/* Filter Header */}

        <FilterHeader
          expanded={expanded}
          setExpanded={() => this.setState({ expanded: !expanded })}
          numberOfFilters={numberOfFilters}
          roleTerm={roleTerm}
          skillTerm={skillTerm}
          roleSelected={roleSelected}
          skillSelected={skillSelected}
        />

        <div className={`${styles.list} ${expanded ? styles.expanded : ''}`}>
          <div className={styles.listGroup}>
            {/* Reset Filter Button */}
            <ResetFilter
              isVisible={isFilterSet(getFilterFromQS(this.props.location))}
              onFiltersReset={this.handleFilterReset}
            />

            {/* Role */}
            <div className={styles.filterItemContainer}>
              <div className={styles.filterTitle}>Role</div>

              <div style={{ maxWidth: '350px' }}>
                <Dropdown
                  // Values
                  value={roleTerm}
                  items={roleSuggestions}
                  placeholder="eg: Graphic Designer"
                  // Rendering & styling
                  showDropdown={roleFocus}
                  highlightIndex={roleHighlightIndex}
                  inputClassname={styles.dropdownInput}
                  activeClassname={styles.activeItem}
                  itemDisplay={this.renderDropdownItem}
                  // Event handlers
                  onClear={() => this.handleRoleSkill(ROLE_TYPE)}
                  onKeyDown={(e) => this.handleDropdownKeyDown(ROLE_TYPE, e)}
                  onSelect={(role) =>
                    this.handleRoleSkill(ROLE_TYPE, role.name)
                  }
                  onFocus={() => this.setState({ roleFocus: true })}
                  onBlur={() => {
                    this.setState({
                      roleFocus: false,
                      roleTerm: roleSelected ? roleTerm : '',
                    });
                  }}
                  onChange={(e) => this.setState({ roleTerm: e.target.value })}
                  onUpdateHighlightIndex={(i) =>
                    this.setState({ roleHighlightIndex: i })
                  }
                  scrollable
                  resultMaxHeight={'200px'}
                />
              </div>
            </div>

            {/* Skill */}
            <div className={styles.filterItemContainer}>
              <div className={styles.filterTitle}>Skill</div>

              <div style={{ maxWidth: '350px' }}>
                <Dropdown
                  // Values
                  value={skillTerm}
                  items={skillSuggestions}
                  placeholder="eg: 3D Animation"
                  // Rendering & styling
                  showDropdown={skillFocus}
                  highlightIndex={skillHighlightIndex}
                  inputClassname={styles.dropdownInput}
                  activeClassname={styles.activeItem}
                  itemDisplay={this.renderDropdownItem}
                  // Event handlers
                  onClear={() => this.handleRoleSkill(SKILL_TYPE)}
                  onKeyDown={(e) => this.handleDropdownKeyDown(SKILL_TYPE, e)}
                  onSelect={(skill) =>
                    this.handleRoleSkill(SKILL_TYPE, skill.name)
                  }
                  onFocus={() => this.setState({ skillFocus: true })}
                  onBlur={() => {
                    this.setState({
                      skillFocus: false,
                      skillTerm: skillSelected ? skillTerm : '',
                    });
                  }}
                  onChange={(e) => this.setState({ skillTerm: e.target.value })}
                  onUpdateHighlightIndex={(i) =>
                    this.setState({ skillHighlightIndex: i })
                  }
                  scrollable
                  resultMaxHeight={'200px'}
                />
              </div>
            </div>

            {/* Day Rate */}
            <div className={styles.filterItemContainer}>
              <div className={styles.filterTitle}>Day Rate</div>

              <DayRateFilter
                currentMin={currentMinRate}
                currentMax={currentMaxRate}
                minDayRate={minDayRate}
                maxDayRate={maxDayRate}
                onCustomAmountChange={this.handleCustomRateChange}
                onSelectDayRate={this.handleSelectDayRate}
                onSelectCustomRate={this.handleSelectCustomRate}
              />
            </div>

            {/* Location */}
            <div className={styles.filterItemContainer}>
              <div className={styles.filterTitle}>Location</div>
              <div style={{ maxWidth: '350px' }}>
                <SingleLocationDropdown
                  defaultLocation={locationTerm}
                  shouldClearOnBlur={!locationSelected}
                  showResetIcon={locationSelected}
                  onLocationReset={this.handleLocationFilterReset}
                  onLocationChange={this.handleSelectLocation}
                  placeholder="Enter the nearest city or country"
                />
              </div>
            </div>
          </div>
        </div>
      </Panel>
    );
  }
}

BrowseFilter.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,

  // Redux - Roles
  allRoles: PropTypes.array,
  loadAllRoles: PropTypes.func,
  loadedAllRoles: PropTypes.bool,
  loadingAllRoles: PropTypes.bool,

  // Redux - Skills
  allSkills: PropTypes.array,
  loadAllSkills: PropTypes.func,
  loadedAllSkills: PropTypes.bool,
  loadingAllSkills: PropTypes.bool,
};
